/**
 * The SPA-side admin gate.
 *
 * THIS IS A DISPLAY DECISION, NOT A SECURITY BOUNDARY, and the distinction is
 * load-bearing rather than pedantic. Every /admin/* route re-checks the role
 * claim on the JWT server-side and returns 403 regardless of what this
 * component concluded. What this buys is that a customer never sees staff
 * navigation, and that a support-role operator lands on an explanation instead
 * of a screen full of failed requests.
 *
 * `role === null` means ORDINARY CUSTOMER — a meaningful value, not a missing
 * one. So the check is `!== null`, never a truthiness test: a future role named
 * with a falsy value would silently lock out its own holders.
 *
 * While /me is in flight we render a spinner rather than the forbidden page.
 * Flashing "you don't have access" at an owner for 200ms every hard refresh is
 * the kind of thing that generates a support ticket about a bug that isn't one.
 */
import type { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useMe } from '@/hooks/useMe';
import { ForbiddenPage } from './ForbiddenPage';

export function RequireAdminRole({ children }: { children: ReactNode }) {
  const { data: me, isLoading, isError } = useMe();

  if (isLoading) {
    return (
      <div className="h-screen grid place-items-center">
        <span className="w-6 h-6 border-2 border-brand-teal border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // /me failing is an auth failure, not a role failure — useMe uses retry:false
  // precisely because a 401 is an answer. Send them to login, not to a 403 that
  // would misdescribe why they are stuck.
  if (isError || !me) return <Navigate to="/login" replace />;

  if (me.user.role === null || me.user.role === undefined) return <ForbiddenPage />;

  return <>{children}</>;
}
